import type { ReactNode } from 'react';
import { T } from 'gt-react';
import { Label } from 'src/components/ui/label';
import { Input } from 'src/components/ui/input';
import { Switch } from 'src/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { useStepTypeConfigSchema } from '@/api/rpc/hooks/workflows/useWorkflowDefinitions';
import type { ConfigRendererProps } from './types';

export type BatchConfig = {
  size?: number;
  concurrency?: number;
  maxWaitMs?: number;
  onItemError?: 'continue' | 'fail-fast';
  /**
   * Persisted but deliberately NOT rendered — the control would be a no-op.
   * `executeWorkflow` only acts on this when the fanout source emits a
   * `__continueAsNew` continuation, and nothing has emitted one since #4455
   * replaced the Observe DataSource Link's per-window continue-as-new with
   * `runInitialIngestPump` (2026-06-27). Kept on the type so an existing
   * config round-trips untouched. Removal tracked as a follow-up.
   */
  continueAsNew?: boolean;
  pageLevelResume?: boolean;
};

/**
 * The step type's own `batch` default, as published on its config JSON Schema
 * (`WorkflowStepBatchConfigSchema.prefault(...)` lands here as `default`).
 *
 * This matters for more than placeholder text. `size` is REQUIRED by the batch
 * schema, so a persisted `batch` missing it fails `getBatchConfig`'s safeParse
 * and the whole object is dropped — the author's other choices with it. Reading
 * the step's default lets an edit to any single field persist something that
 * parses, and lets the controls show what will actually run rather than the
 * engine-wide default (`use_data_mapping` defaults `onItemError` to `continue`,
 * not `fail-fast`).
 */
/** One level of narrowing: `value[key]` when `value` is a non-null object. */
function objectProp(value: unknown, key: string): unknown {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)[key]
    : undefined;
}

function stepTypeBatchDefault(schema: unknown): BatchConfig {
  const value = objectProp(
    objectProp(objectProp(schema, 'properties'), 'batch'),
    'default',
  );
  return typeof value === 'object' && value !== null
    ? (value as BatchConfig)
    : {};
}

interface BatchOptionsFieldsProps extends Pick<
  ConfigRendererProps,
  'config' | 'onConfigChange' | 'readOnly' | 'node'
> {
  /** Helper copy under the fields. */
  helpText?: ReactNode;
  /**
   * Show the change-stream window control (max wait). Only meaningful on the
   * Observe→Upsert steps that consume a change stream; a step batched purely
   * for fanout (e.g. Use Data Mapping) has no window.
   */
  showWindowFields?: boolean;
  /**
   * Render the "Batching options" heading. Turn off when the caller already
   * labels the section (e.g. a collapsible whose trigger says "Batching").
   */
  showHeading?: boolean;
}

/**
 * One numeric `batch` field. Extracted because all three are the same control
 * differing only in id, label, bounds and which default seeds the placeholder.
 */
function BatchNumberField({
  id,
  label,
  min,
  value,
  fallbackPlaceholder,
  defaultValue,
  disabled,
  onChange,
}: {
  id: string;
  label: ReactNode;
  min: number;
  value: number | undefined;
  fallbackPlaceholder: string;
  defaultValue: number | undefined;
  disabled?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        min={min}
        value={typeof value === 'number' ? value : ''}
        placeholder={
          defaultValue !== undefined
            ? String(defaultValue)
            : fallbackPlaceholder
        }
        disabled={disabled}
        onChange={onChange}
        className="w-28 text-sm"
      />
    </div>
  );
}

/**
 * The shared `batch` fields, without a section header — see
 * `WorkflowStepBatchConfigSchema`. Rendered once, in the step panel's Advanced
 * section, so every batched step gets the same controls in the same place.
 */
export function BatchOptionsFields({
  config,
  onConfigChange,
  readOnly,
  node,
  helpText,
  showWindowFields = true,
  showHeading = true,
}: BatchOptionsFieldsProps) {
  const batch = (config.batch as BatchConfig | undefined) ?? {};
  // `listStepTypes` omits config schemas by default, so the node usually does
  // NOT carry one — reading only the inline copy would miss every step's real
  // default (e.g. 5000 for the upserts), showing the wrong placeholder and, worse,
  // locking the controls behind `sizeRequired`. Same fallback StandardConfigRenderer
  // uses; callers that request schemas up front never fetch.
  const inlineSchema = node.data._stepTypeData?.configSchema;
  const { data: fetchedSchema } = useStepTypeConfigSchema(
    inlineSchema ? undefined : node.data.stepType,
  );
  const defaults = stepTypeBatchDefault(inlineSchema ?? fetchedSchema);
  // `size` is REQUIRED by the batch schema, so a `batch` without one is dropped
  // wholesale at runtime — the step silently reverts to per-item fan-out. When
  // the step type supplies no default there is no honest value to write, so the
  // rest of the controls are locked until an author sets a size. That makes the
  // unparseable state unreachable rather than merely warned about; a blank batch
  // stays blank, which is itself meaningful (blank = per-item).
  const sizeRequired = batch.size === undefined && defaults.size === undefined;
  const fieldsDisabled = readOnly || sizeRequired;

  function setBatch(patch: Partial<BatchConfig>) {
    const next: BatchConfig = { ...batch, ...patch };
    // Keep the persisted object parseable: an author editing any one control
    // must not produce a `batch` that the runtime throws away entirely.
    if (next.size === undefined && defaults.size !== undefined) {
      next.size = defaults.size;
    }
    // `onConfigChange` may return a promise (dynamic socket regeneration);
    // nothing here depends on it, matching the other config renderers.
    void onConfigChange({ ...config, batch: next });
  }

  function numberHandler(
    key: 'size' | 'concurrency' | 'maxWaitMs',
    min: number,
  ) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const n = parseInt(e.target.value, 10);
      setBatch({ [key]: Number.isFinite(n) && n >= min ? n : undefined });
    };
  }

  return (
    <div className="space-y-2">
      {showHeading && (
        <Label className="text-sm font-medium">
          <T context="label for batching options section on upsert step">
            Batching options
          </T>
        </Label>
      )}
      <div className="flex flex-wrap gap-4">
        <BatchNumberField
          id="batch-size"
          label={
            <T context="label for upsert step batch size input">Batch size</T>
          }
          min={1}
          value={batch.size}
          fallbackPlaceholder="500"
          defaultValue={defaults.size}
          disabled={readOnly}
          onChange={numberHandler('size', 1)}
        />
        <BatchNumberField
          id="batch-concurrency"
          label={
            <T context="label for upsert step batch concurrency input">
              Concurrency
            </T>
          }
          min={1}
          value={batch.concurrency}
          fallbackPlaceholder="1"
          defaultValue={defaults.concurrency}
          disabled={fieldsDisabled}
          onChange={numberHandler('concurrency', 1)}
        />
        {showWindowFields && (
          <BatchNumberField
            id="batch-max-wait"
            label={
              <T context="label for upsert step batch max wait input">
                Max wait (ms)
              </T>
            }
            min={0}
            value={batch.maxWaitMs}
            fallbackPlaceholder="2000"
            defaultValue={defaults.maxWaitMs}
            disabled={fieldsDisabled}
            onChange={numberHandler('maxWaitMs', 0)}
          />
        )}
        <div className="space-y-1">
          <Label
            htmlFor="batch-on-item-error"
            className="text-xs text-muted-foreground"
          >
            <T context="label for upsert step batch on item error select">
              On item error
            </T>
          </Label>
          <Select
            value={batch.onItemError ?? defaults.onItemError ?? 'fail-fast'}
            disabled={fieldsDisabled}
            onValueChange={(value) =>
              setBatch({ onItemError: value as BatchConfig['onItemError'] })
            }
          >
            <SelectTrigger id="batch-on-item-error" className="w-36 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fail-fast">
                <T context="upsert step on item error fail-fast option">
                  Fail fast
                </T>
              </SelectItem>
              <SelectItem value="continue">
                <T context="upsert step on item error continue option">
                  Continue
                </T>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-start gap-2 pt-1">
        <Switch
          id="batch-page-level-resume"
          checked={batch.pageLevelResume ?? defaults.pageLevelResume ?? false}
          disabled={fieldsDisabled}
          onCheckedChange={(checked) => setBatch({ pageLevelResume: checked })}
          className="mt-0.5"
        />
        <div className="space-y-0.5">
          <Label
            htmlFor="batch-page-level-resume"
            className="text-sm cursor-pointer"
          >
            <T context="label for step batch page level resume switch">
              One run record per batch
            </T>
          </Label>
          <p className="text-xs text-muted-foreground">
            <T context="helper text for step batch page level resume switch">
              Records one run row for the whole batch instead of one per row,
              which is far faster on large data. The cost is that a retry
              re-runs every row in the batch, so only turn this on when
              repeating a row is harmless — a lookup or a keyed upsert. Leave it
              off for anything that sends, charges, or appends.
            </T>
          </p>
        </div>
      </div>
      {sizeRequired && !readOnly && (
        <p className="text-xs text-muted-foreground">
          <T context="hint that batch size must be set before other batching options">
            Set a Batch size to enable the other options — a batch config
            without one is ignored at run time.
          </T>
        </p>
      )}
      {helpText !== undefined && (
        <p className="text-xs text-muted-foreground">{helpText}</p>
      )}
    </div>
  );
}
